import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Table, Button, Space, Modal, Tag, Input, Tooltip, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { SUBSCRIBER_TYPE_LABELS } from '../../models/types';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import SubscriberForm from './SubscriberForm';
import SubscriberDetails from './SubscriberDetails';

const SubscriberList = () => {
  const { subscribers, deleteSubscriber, addSubscriber, updateSubscriber } = useApp();
  const { user } = useAuth();
  const tableRef = useRef();
  const [appName, setAppName] = useState('');
  const isViewer = user?.role === 'viewer';

  useEffect(() => {
    // Récupère le nom de l'application depuis la configuration (storageService)
    import('../../services/storageService').then(({ default: storageService }) => {
      storageService.getSetting('app_name', 'Gestion EAU').then(setAppName);
    });
  }, []);

  // Fonction d'export PDF
  const handleExportPDF = async () => {
    // Crée un clone du tableau sans la colonne Actions ni ID
    const tableNode = tableRef.current.cloneNode(true);
    // Supprime la colonne Actions et ID de l'en-tête
    const ths = tableNode.querySelectorAll('th');
    let idIdx = -1, actionIdx = -1;
    ths.forEach((th, idx) => {
      const txt = th.innerText.toLowerCase();
      if (txt.includes('action')) actionIdx = idx;
      if (txt === 'id') idIdx = idx;
    });
    // Supprime d'abord la colonne la plus à droite pour ne pas décaler les index
    [actionIdx, idIdx].filter(i => i !== -1).sort((a, b) => b - a).forEach(idx => {
      ths[idx]?.parentNode.removeChild(ths[idx]);
      tableNode.querySelectorAll('tr').forEach(tr => {
        if (tr.children[idx]) tr.removeChild(tr.children[idx]);
      });
    });
    // Ajoute le titre et l'entête au-dessus du tableau
    const wrapper = document.createElement('div');
    wrapper.style.padding = '24px';
    const appTitle = document.createElement('h2');
    appTitle.innerText = appName || 'Gestion EAU';
    appTitle.style.textAlign = 'center';
    appTitle.style.marginBottom = '8px';
    const tableTitle = document.createElement('h3');
    tableTitle.innerText = 'Liste des abonnés';
    tableTitle.style.textAlign = 'center';
    tableTitle.style.marginBottom = '16px';
    wrapper.appendChild(appTitle);
    wrapper.appendChild(tableTitle);
    wrapper.appendChild(tableNode);
    document.body.appendChild(wrapper);
    // Utilise html2canvas pour capturer le rendu
    const canvas = await html2canvas(wrapper, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
    const x = (pageWidth - imgWidth * ratio) / 2;
    const y = 20;
    pdf.addImage(imgData, 'PNG', x, y, imgWidth * ratio, imgHeight * ratio);
    pdf.save('liste_abonnes.pdf');
    document.body.removeChild(wrapper);
  };
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [searchText, setSearchText] = useState('');

  const handleAddSubscriber = () => {
    setSelectedSubscriber(null);
    setIsFormOpen(true);
  };

  const handleEditSubscriber = (record) => {
    setSelectedSubscriber(record);
    setIsFormOpen(true);
  };

  const handleViewDetails = (record) => {
    setSelectedSubscriber(record);
    setIsDetailsOpen(true);
  };

  const handleDeleteSubscriber = (id) => {
    Modal.confirm({
      title: 'Supprimer cet abonné',
      content: 'Êtes-vous sûr de vouloir supprimer cet abonné ?',
      okText: 'Oui',
      cancelText: 'Non',
      okType: 'danger',
      onOk() {
        deleteSubscriber(id, user?.id);
      }
    });
  };

  const handleFormSubmit = (values) => {
    if (selectedSubscriber) {
      updateSubscriber(selectedSubscriber.id, values, user?.id);
    } else {
      addSubscriber(values, user?.id);
    }
    setIsFormOpen(false);
    setSelectedSubscriber(null);
  };

  const filteredData = subscribers.filter(subscriber =>
    subscriber.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
    subscriber.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
    subscriber.phone.includes(searchText) ||
    subscriber.cin.includes(searchText)
  );

  const columns = [
    {
      title: 'Nom complet',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'CIN',
      dataIndex: 'cin',
      key: 'cin'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'individual' ? 'blue' : type === 'professional' ? 'orange' : 'purple'}>
          {SUBSCRIBER_TYPE_LABELS[type]}
        </Tag>
      )
    },
    {
      title: 'Ville',
      dataIndex: 'city',
      key: 'city'
    },
    {
      title: 'Caution',
      key: 'caution',
      render: (_, record) => (
        <Tag color={record.cautionPaid ? 'green' : 'red'}>
          {record.caution} FCFA {record.cautionPaid ? '✓' : '✗'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {!isViewer && (
            <>
              <Tooltip title="Modifier">
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditSubscriber(record)}
                />
              </Tooltip>
              <Tooltip title="Supprimer">
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteSubscriber(record.id)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {isViewer && (
        <Alert
          message="Mode Consultation"
          description="Vous êtes en mode lecture seule. Les modifications ne sont pas autorisées pour votre rôle."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}
      <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
        <Input
          placeholder="Rechercher (nom, téléphone, CIN)..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1 }}
        />
        {!isViewer && (
          <>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddSubscriber}>
              Ajouter un abonné
            </Button>
            <Button onClick={handleExportPDF} type="default" style={{ marginLeft: 8 }}>
              Exporter PDF
            </Button>
          </>
        )}
      </div>

      <div ref={tableRef}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{ pageSize: 10, total: filteredData.length }}
        />
      </div>

      <SubscriberForm
        visible={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        initialValues={selectedSubscriber}
      />

      <SubscriberDetails
        visible={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        subscriber={selectedSubscriber}
      />
    </div>
  );
};

export default SubscriberList;
